import { useState, useRef, useCallback, useEffect } from 'react';
import { VoiceRecorder } from '@independo/capacitor-voice-recorder';

/**
 * Custom hook for managing audio recording.
 *
 * Native (iOS/Android): @independo/capacitor-voice-recorder. The plugin's
 * AVAudioRecorder runs in the HOST APP process, so it survives WKWebView
 * web-content-process termination during long screen-locked recordings.
 * If iOS evicts the JS context while recording, the on-disk audio file
 * keeps growing; on the next launch we ask the plugin getCurrentStatus(),
 * and if it reports RECORDING/PAUSED/INTERRUPTED we rehydrate state and
 * the user can stop normally to recover the audio.
 *
 * Web: MediaRecorder fallback (no recovery — page reload destroys the blob).
 *
 * Pass `{ recover: true }` to skip startRecording on mount and rehydrate
 * from a previously-persisted localStorage marker instead.
 */

function isNativePlatform() {
  return !!window.Capacitor?.isNativePlatform?.();
}

const RECORDING_MARKER_KEY = 'clarime_active_recording';
const WATCHDOG_INTERVAL_MS = 10000;

function readMarker() {
  try {
    const raw = localStorage.getItem(RECORDING_MARKER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeMarker(marker) {
  try {
    localStorage.setItem(RECORDING_MARKER_KEY, JSON.stringify(marker));
  } catch {
    // Quota or private mode — recovery degrades, recording itself still works
  }
}

function clearMarker() {
  try {
    localStorage.removeItem(RECORDING_MARKER_KEY);
  } catch {
    // ignore
  }
}

/**
 * Probe the native plugin for an in-progress recording. Call this on app
 * launch (in App.jsx) before deciding which view to render. Returns null
 * on web, when no marker is present, or when the plugin reports NONE.
 *
 * Returns { status, startedAt, pausedAt, pausedMs } when the recorder is
 * still alive in the host app process. The caller should route the user
 * to the recording screen with `{ recover: <return value> }`.
 *
 * If a stale marker is found with status NONE, clears the marker and
 * returns { lost: true, startedAt } so the UI can warn the user.
 */
export async function checkActiveNativeRecording() {
  if (!isNativePlatform()) return null;
  const marker = readMarker();
  if (!marker) return null;

  let status = 'NONE';
  try {
    const result = await VoiceRecorder.getCurrentStatus();
    status = result?.status || 'NONE';
  } catch (err) {
    console.warn('[ClariMe Recording] getCurrentStatus failed:', err);
    status = 'NONE';
  }

  if (status === 'NONE') {
    // Native recorder is gone (app fully terminated, etc.) — recording is lost.
    clearMarker();
    return { lost: true, startedAt: marker.startedAt };
  }

  return {
    status,
    startedAt: marker.startedAt,
    pausedAt: marker.pausedAt || null,
    pausedMs: marker.pausedMs || 0,
  };
}

export default function useAudioRecorder({ recover = null } = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [error, setError] = useState(null);
  const [isNativeRecording, setIsNativeRecording] = useState(false);
  const [interruptionStatus, setInterruptionStatus] = useState(null); // null | 'began' | 'ended'

  // Shared refs
  const timerRef = useRef(null);
  const watchdogRef = useRef(null);
  const isRecordingRef = useRef(false);
  const interruptionListenersRef = useRef([]);

  // Timestamp-based duration tracking (survives background suspension)
  const startedAtRef = useRef(null);
  const pausedAtRef = useRef(null);
  const pausedMsRef = useRef(0);

  // Web-only refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Persist current state to localStorage so we can recover after a JS reset
  const persistMarker = useCallback(() => {
    if (!isNativePlatform() || !startedAtRef.current) return;
    writeMarker({
      startedAt: startedAtRef.current,
      pausedAt: pausedAtRef.current,
      pausedMs: pausedMsRef.current,
      version: 1,
    });
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (watchdogRef.current) clearInterval(watchdogRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      // Tear down interruption listeners
      interruptionListenersRef.current.forEach(handle => {
        try { handle.remove?.(); } catch { /* ignore */ }
      });
      interruptionListenersRef.current = [];
    };
  }, []);

  // Timestamp-based duration calculation (auto-corrects after background)
  const calcDuration = useCallback(() => {
    if (!startedAtRef.current) return 0;
    const now = pausedAtRef.current || Date.now();
    return Math.floor((now - startedAtRef.current - pausedMsRef.current) / 1000);
  }, []);

  // Start the display timer (uses timestamps, not increment)
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setDuration(calcDuration());
    }, 1000);
  }, [calcDuration]);

  // Watchdog: while recording, periodically ask the plugin "are you still
  // recording?" and surface a UI warning if it silently went to NONE.
  const startWatchdog = useCallback(() => {
    if (watchdogRef.current) clearInterval(watchdogRef.current);
    watchdogRef.current = setInterval(async () => {
      if (!isRecordingRef.current) return;
      try {
        const result = await VoiceRecorder.getCurrentStatus();
        const status = result?.status || 'NONE';
        if (status === 'NONE') {
          console.error('[ClariMe Recording] Watchdog: native status went to NONE');
          setError('Recording was unexpectedly stopped by the system.');
          isRecordingRef.current = false;
          if (timerRef.current) clearInterval(timerRef.current);
          if (watchdogRef.current) clearInterval(watchdogRef.current);
        } else if (status === 'INTERRUPTED') {
          setInterruptionStatus('began');
        }
      } catch (err) {
        console.warn('[ClariMe Recording] Watchdog query failed:', err);
      }
    }, WATCHDOG_INTERVAL_MS);
  }, []);

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current) clearInterval(watchdogRef.current);
    watchdogRef.current = null;
  }, []);

  // Register native interruption listeners so the UI can react when iOS
  // pauses us for a phone call / Siri / alarm.
  const registerInterruptionListeners = useCallback(async () => {
    if (!isNativePlatform()) return;
    // Tear down any previous handles first
    interruptionListenersRef.current.forEach(h => { try { h.remove?.(); } catch { /* ignore */ } });
    interruptionListenersRef.current = [];
    try {
      const beganHandle = await VoiceRecorder.addListener('voiceRecordingInterrupted', () => {
        console.log('[ClariMe Recording] Interruption began');
        setInterruptionStatus('began');
        setIsPaused(true);
        if (timerRef.current) clearInterval(timerRef.current);
        pausedAtRef.current = Date.now();
        persistMarker();
      });
      const endedHandle = await VoiceRecorder.addListener('voiceRecordingInterruptionEnded', () => {
        console.log('[ClariMe Recording] Interruption ended');
        setInterruptionStatus('ended');
        // Plugin leaves status at INTERRUPTED until we explicitly resume.
        // Don't auto-resume — let the user decide so they can verify.
      });
      interruptionListenersRef.current = [beganHandle, endedHandle].filter(Boolean);
    } catch (err) {
      console.warn('[ClariMe Recording] Could not register interruption listeners:', err);
    }
  }, [persistMarker]);

  // ─── Native Recording (Capacitor VoiceRecorder plugin) ───────────

  const startNativeRecording = useCallback(async () => {
    try {
      setError(null);
      setAudioBlob(null);
      setInterruptionStatus(null);

      // Check/request permission
      const permResult = await VoiceRecorder.requestAudioRecordingPermission();
      if (!permResult.value) {
        throw new Error('Microphone permission denied');
      }

      await VoiceRecorder.startRecording();

      console.log('[ClariMe Recording] Native recording started');

      isRecordingRef.current = true;
      setIsRecording(true);
      setIsNativeRecording(true);
      setIsPaused(false);
      setDuration(0);

      startedAtRef.current = Date.now();
      pausedAtRef.current = null;
      pausedMsRef.current = 0;

      persistMarker();
      await registerInterruptionListeners();
      startTimer();
      startWatchdog();
    } catch (err) {
      console.error('[ClariMe Recording] Native start failed:', err);
      setError(err.message || 'Failed to start recording');
    }
  }, [startTimer, startWatchdog, persistMarker, registerInterruptionListeners]);

  const stopNativeRecording = useCallback(async () => {
    isRecordingRef.current = false;

    if (timerRef.current) clearInterval(timerRef.current);
    stopWatchdog();

    try {
      const result = await VoiceRecorder.stopRecording();

      const { recordDataBase64, mimeType: nativeMimeType, msDuration } = result.value;
      const mimeType = nativeMimeType || 'audio/aac';

      if (!recordDataBase64) {
        throw new Error('Recorder returned no audio data');
      }

      console.log('[ClariMe Recording] Native stopped:', {
        mimeType,
        durationMs: msDuration,
        base64Length: recordDataBase64.length,
      });

      // Convert base64 to Blob for existing upload pipeline
      const byteString = atob(recordDataBase64);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });

      console.log('[ClariMe Recording] Blob created:', {
        type: blob.type,
        size: `${(blob.size / 1024).toFixed(1)} KB`,
      });

      // Update duration from actual native duration
      const finalDuration = Math.floor((msDuration || (Date.now() - startedAtRef.current - pausedMsRef.current)) / 1000);
      setDuration(finalDuration);
      setAudioBlob(blob);
    } catch (err) {
      console.error('[ClariMe Recording] Native stop failed:', err);
      setError(err.message || err.code || 'Failed to stop recording');
    }

    clearMarker();
    setIsRecording(false);
    setIsPaused(false);
    setIsNativeRecording(false);
    setAudioLevel(0);
    setInterruptionStatus(null);
    startedAtRef.current = null;
    // Tear down interruption listeners
    interruptionListenersRef.current.forEach(h => { try { h.remove?.(); } catch { /* ignore */ } });
    interruptionListenersRef.current = [];
  }, [stopWatchdog]);

  const pauseNativeRecording = useCallback(async () => {
    try {
      await VoiceRecorder.pauseRecording();
      if (timerRef.current) clearInterval(timerRef.current);
      pausedAtRef.current = Date.now();
      setDuration(calcDuration());
      setIsPaused(true);
      persistMarker();
    } catch (err) {
      console.error('[ClariMe Recording] Native pause failed:', err);
    }
  }, [calcDuration, persistMarker]);

  const resumeNativeRecording = useCallback(async () => {
    try {
      await VoiceRecorder.resumeRecording();
      pausedMsRef.current += Date.now() - (pausedAtRef.current || Date.now());
      pausedAtRef.current = null;
      setInterruptionStatus(null);
      persistMarker();
      startTimer();
      setIsPaused(false);
    } catch (err) {
      console.error('[ClariMe Recording] Native resume failed:', err);
    }
  }, [startTimer, persistMarker]);

  // Rehydrate state when the hook mounts in recovery mode (post JS-reset).
  // The native recorder is still alive in the host app process; we just
  // need to re-establish JS state and listeners pointing at it.
  const rehydrateFromRecovery = useCallback(async (recoveryData) => {
    try {
      console.log('[ClariMe Recording] Rehydrating from recovery:', recoveryData);
      startedAtRef.current = recoveryData.startedAt;
      pausedAtRef.current = recoveryData.pausedAt;
      pausedMsRef.current = recoveryData.pausedMs;

      const status = recoveryData.status;
      const isPausedNow = status === 'PAUSED' || status === 'INTERRUPTED';

      isRecordingRef.current = true;
      setIsRecording(true);
      setIsNativeRecording(true);
      setIsPaused(isPausedNow);
      setDuration(calcDuration());

      if (status === 'INTERRUPTED') {
        setInterruptionStatus('began');
      }

      await registerInterruptionListeners();
      if (!isPausedNow) {
        startTimer();
      }
      startWatchdog();
    } catch (err) {
      console.error('[ClariMe Recording] Rehydration failed:', err);
      setError('Could not recover the in-progress recording.');
    }
  }, [calcDuration, startTimer, startWatchdog, registerInterruptionListeners]);

  // ─── Web Recording (MediaRecorder API) ───────────────────────────

  const startWebRecording = useCallback(async () => {
    try {
      setError(null);
      setAudioBlob(null);
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      streamRef.current = stream;

      // Set up audio level monitoring
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        if (!isRecordingRef.current) {
          setAudioLevel(0);
          return;
        }
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 255);
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };

      // Create MediaRecorder with best available format
      const mimePreference = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/aac',
        '',
      ];

      let mimeType = '';
      for (const candidate of mimePreference) {
        if (!candidate || MediaRecorder.isTypeSupported(candidate)) {
          mimeType = candidate;
          break;
        }
      }

      console.log('[ClariMe Recording] Web: Selected mime type:', mimeType || '(browser default)');

      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const finalMimeType = mediaRecorder.mimeType || mimeType || 'audio/mp4';
        const blob = new Blob(audioChunksRef.current, { type: finalMimeType });

        console.log('[ClariMe Recording] Web stopped. Blob:', {
          type: finalMimeType,
          size: `${(blob.size / 1024).toFixed(1)} KB`,
          chunks: audioChunksRef.current.length,
        });

        setAudioBlob(blob);
      };

      mediaRecorder.start(1000);
      isRecordingRef.current = true;
      setIsRecording(true);
      setIsNativeRecording(false);
      setIsPaused(false);
      setDuration(0);

      startedAtRef.current = Date.now();
      pausedAtRef.current = null;
      pausedMsRef.current = 0;

      startTimer();
      updateLevel();

    } catch (err) {
      console.error('[ClariMe Recording] Web start failed:', err);
      setError(err.message || 'Failed to access microphone');
    }
  }, [startTimer]);

  const stopWebRecording = useCallback(() => {
    isRecordingRef.current = false;

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) clearInterval(timerRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }

    setIsRecording(false);
    setIsPaused(false);
    setIsNativeRecording(false);
    setAudioLevel(0);
    startedAtRef.current = null;
  }, []);

  const pauseWebRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      if (timerRef.current) clearInterval(timerRef.current);
      pausedAtRef.current = Date.now();
      setDuration(calcDuration());
      setIsPaused(true);
    }
  }, [calcDuration]);

  const resumeWebRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      pausedMsRef.current += Date.now() - (pausedAtRef.current || Date.now());
      pausedAtRef.current = null;
      startTimer();
      setIsPaused(false);
    }
  }, [startTimer]);

  // ─── Public API (routes to native or web) ────────────────────────

  const useNative = isNativePlatform();

  const startRecording = useCallback(async () => {
    if (useNative) {
      await startNativeRecording();
    } else {
      await startWebRecording();
    }
  }, [useNative, startNativeRecording, startWebRecording]);

  const stopRecording = useCallback(async () => {
    if (useNative) {
      await stopNativeRecording();
    } else {
      stopWebRecording();
    }
  }, [useNative, stopNativeRecording, stopWebRecording]);

  const pauseRecording = useCallback(async () => {
    if (useNative) {
      await pauseNativeRecording();
    } else {
      pauseWebRecording();
    }
  }, [useNative, pauseNativeRecording, pauseWebRecording]);

  const resumeRecording = useCallback(async () => {
    if (useNative) {
      await resumeNativeRecording();
    } else {
      resumeWebRecording();
    }
  }, [useNative, resumeNativeRecording, resumeWebRecording]);

  // If mounted with `recover` data, rehydrate immediately. Native only.
  useEffect(() => {
    if (!recover || !useNative) return;
    rehydrateFromRecovery(recover);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalculate duration when app returns from background
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isRecordingRef.current && !pausedAtRef.current) {
        setDuration(calcDuration());
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [calcDuration]);

  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    isRecording,
    isPaused,
    duration,
    formattedDuration: formatDuration(duration),
    audioLevel,
    audioBlob,
    error,
    isNativeRecording,
    interruptionStatus,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording
  };
}
